import React, { useContext } from 'react';

import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { RentalRedirector } from '@thd-nucleus/app-render';

import { Col, Row } from '@thd-olt-component-react/core-ui';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  ProductInformation,
  RentalReservationFormProviders,
  ReservationForm
} from '@thd-olt-component-react/rental-reservation-form';

import { SkipForNowButton } from '../components/SkipForNowButton';
import { ProductMetadata } from '../components/ProductMetadata';
import { getConfigLists, useFeatureCheck, enabledBoolOrListCheck } from '../helpers';

import '../styles/pages/Reservation.scss';

export const Reservation = () => {
  const { channel, segment = '', isDebugMode } = useContext(ExperienceContext);
  const localizedStore = useStore({ online: false });

  const { itemId } = useParams();
  const isMobile = channel === 'mobile';

  const {
    whitelistedStores = [],
    blacklistedItems = [],
    rentalEstimateEnabled = false,
    contractTermsEnabled = false,
    damageProtectionEnabled = false,
    fdaV4Enabled = false,
    estimateGraphQlEnabled = false,
    pricingGraphQlEnabled = false,
    reservationGraphQlEnabled = false,
  } = getConfigLists();

  const estimateEnabled = useFeatureCheck(rentalEstimateEnabled)
  const isContractTermsEnabled = useFeatureCheck(contractTermsEnabled)
  const isDamageProtectionEnabled = useFeatureCheck(damageProtectionEnabled, localizedStore?.storeId?.padStart(4, '0'))
  const isFdaV4Enabled = useFeatureCheck(fdaV4Enabled, localizedStore?.storeId?.padStart(4, '0'))
  const isEstimateGraphQlEnabled = useFeatureCheck(estimateGraphQlEnabled, localizedStore?.storeId?.padStart(4, '0'))
  const isPricingGraphQlEnabled = useFeatureCheck(pricingGraphQlEnabled, localizedStore?.storeId?.padStart(4, '0'))
  const isReservationGraphQlEnabled = useFeatureCheck(
    reservationGraphQlEnabled,
    localizedStore?.storeId?.padStart(4, '0')
  )

  const handleContinueReservationButtonClick = (storeNumber) => {
    if (itemId && typeof window !== 'undefined') {
      window.location.href = '/checkout/toolrental';
    }
  };

  return (
    <QueryProvider
      defaultVariables={{ dataSource: 'catalog', fetchPolicy: 'cache-first', skipKPF: true }}
      cacheKey={`rental-reservations-${isMobile ? 'mobile' : 'desktop'}`}
    >
      <Row className="isBound">
        <ErrorBoundary name="redirector">
          <RentalRedirector />
        </ErrorBoundary>
        <ErrorBoundary name="product-metadata">
          <ProductMetadata itemId={itemId} pageType="reservations" />
        </ErrorBoundary>
        <ErrorBoundary name="breadcrumbs">
          <Col sm="10">
            <Breadcrumbs
              itemId={itemId}
              showInternet={isMobile}
            />
          </Col>
          { !isMobile && (
            <Col sm="2" className="skip-button">
              <SkipForNowButton itemId={itemId} />
            </Col>
          )}
        </ErrorBoundary>
        <ErrorBoundary name="rental-reservation-form" className="rental-reservation-form">
          <Row>
            <Col sm="5">
              <ProductInformation itemId={itemId} pricingGraphQlEnabled={isPricingGraphQlEnabled} />
            </Col>
            <Col sm="7" className="right-card">
              <RentalReservationFormProviders
                whitelistedStores={whitelistedStores}
                blacklistedItems={blacklistedItems}
                rentalEstimateEnabled={estimateEnabled}
                contractTermsEnabled={isContractTermsEnabled}
                damageProtectionEnabled={isDamageProtectionEnabled}
                FDAv4Enabled={isFdaV4Enabled}
                estimateGraphQlEnabled={isEstimateGraphQlEnabled}
                graphQlEnabled={isReservationGraphQlEnabled}
              >
                <ReservationForm itemId={itemId} handleButtonClick={handleContinueReservationButtonClick} />
              </RentalReservationFormProviders>
            </Col>
          </Row>
        </ErrorBoundary>
      </Row>
    </QueryProvider>
  );
}

Reservation.displayName = 'ReservationPage';
Reservation.propTypes = {};
Reservation.defaultProps = {};
